'use client'

import { clsx } from 'clsx'
import { type FC, useEffect } from 'react'
import { useIntl } from 'react-intl'

import { IconInfo } from '@redteclab/icons'

import { Dialog, DialogContent, DialogTitle } from 'base-ui'

import { useOpenableControls } from '../../../generic-hooks/useOpenableControls/useOpenableControls'
import { useGlobalConfigContext } from '../../../global-config/context/globalConfigContext'
import { useProductStockInformationStatus } from '../../../product-page/components/ProductBuyBox/hooks/useProductStockInformationStatus'
import {
  EXPERIMENTS_TRACK_EVENT_PDP_STOCK_INFORMATION,
  experimentsTrackEvent,
} from '../../eventTracking'
import { EXPERIMENT_NAME_PDP_STOCK_INFORMATION } from '../../model/EXPERIMENT_NAME'
import { EXPERIMENT_VARIATION } from '../../model/EXPERIMENT_VARIATION'
import { useExperimentsContext } from '../ExperimentsContext'

import { StatusItem } from './StatusItem'
import { type StatusItemType } from './translations'
import { useExperimentStockInformationConfig } from './useExperimentStockInformationConfig'

const getGroupsOfStatuses = (statuses: StatusItemType[]): number[] => {
  const groupsArray: number[] = []
  for (const statusItem of statuses) {
    if (statusItem.group) {
      groupsArray.push(statusItem.group)
    }
  }

  return [...new Set(groupsArray)].sort((valueA, valueB) => valueA - valueB)
}

const ExperimentStockInformation: FC<{
  experimentVariation?: EXPERIMENT_VARIATION
}> = ({ experimentVariation }) => {
  const intl = useIntl()
  const { addExperimentKeysForActivation } = useExperimentsContext()
  const config = useGlobalConfigContext()
  const popupDialogControls = useOpenableControls(false)
  const productStockInformationStatus = useProductStockInformationStatus()

  useEffect(() => {
    addExperimentKeysForActivation(EXPERIMENT_NAME_PDP_STOCK_INFORMATION)
  }, [addExperimentKeysForActivation, popupDialogControls])

  const widgetControls = useExperimentStockInformationConfig()
  const groupsOfStatuses = getGroupsOfStatuses(widgetControls.statuses)

  return (
    <>
      <button
        className={clsx(
          'font-medium',
          productStockInformationStatus.color,
          'cursor-help',
        )}
        data-qa-id="product-status-qa-id"
        onClick={(): void => {
          popupDialogControls.handleOpen()
          experimentsTrackEvent(
            config,
            EXPERIMENTS_TRACK_EVENT_PDP_STOCK_INFORMATION,
          )
        }}
        type="button"
      >
        {productStockInformationStatus.message}
        {productStockInformationStatus.message ? (
          <IconInfo className="ml-1 inline" />
        ) : null}
      </button>

      <Dialog
        onClose={popupDialogControls.handleClose}
        open={popupDialogControls.isOpen}
        slotProps={{
          dialog: {
            className:
              experimentVariation === EXPERIMENT_VARIATION.V1
                ? 'max-w-5xl'
                : '',
          },
        }}
      >
        <DialogTitle
          onClose={popupDialogControls.handleClose}
          slotProps={{
            closeButton: {
              'aria-label': intl.formatMessage({
                id: 'general.link.label.close',
              }),
            },
          }}
        >
          <div className="block text-center">
            {widgetControls.headerTitle.title}
          </div>
        </DialogTitle>
        <DialogContent className="flex flex-col gap-4">
          <p className="text-s font-medium">
            {widgetControls.headerTitle.subtitle}
          </p>
          {groupsOfStatuses.map((statusGroup: number) => {
            return (
              <ul
                className="flex flex-col rounded-lg bg-light-primary-low p-3 tablet:gap-4"
                key={`group-${statusGroup}`}
              >
                {widgetControls.statuses.map((statusItem: StatusItemType) => {
                  return statusItem.group === statusGroup ? (
                    <StatusItem item={statusItem} />
                  ) : null
                })}
              </ul>
            )
          })}
        </DialogContent>
      </Dialog>
    </>
  )
}

export { ExperimentStockInformation }
